import React, { FC, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { CustomPopover } from 'components';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import {
  BinIcon,
  CheckboxTickIcon,
  DirectionsIcon,
  GreenTickIcon,
  HyphenIcon,
  ListActionsIcon,
  ScheduleJobIcon,
  SetInProgressIcon,
  SortByIcon,
  VerticalListActionsIcon
} from 'assets/icons';

import { JobListItem } from '../jobs.model';
import { getAddressAsString, getJobStatusColor, getJobStatusIconForGroupedJobs, getJobStatusText } from '../utils';
import DateUtils from 'utils/dateUtils';
import { JobLeadSourceTypes, JobStatus } from '../constant';
import { openInNewTab } from 'utils/commonUtils';
import { GOOGLE_MAPS_BASE_URL } from 'config/index';
import EditScheduleModal from './EditScheduleModal';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { DeleteJobConfirmationModal } from './DeleteJobConfrimationModal';
import { useDeleteJobMutation, useEditJobScheduleMutation } from '../api';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { JobsCleverTapEvents } from '../events';
import { logAnalyticEvent } from 'utils/analytics';
import { getSortTooltipText } from 'containers/accounting/pay-runs/utils';
import { PopoverArrowTypes } from 'constants/common';
import DynamicTagsDisplay, { TAGS_CONTAINER_HEIGHT } from './DynamicTagsDisplay';
import { ColumnConfig } from './EditTableModal';
import { API_RESPONSES } from 'types/baseTypes';
import { useStickyTableHeader } from '../hooks/useStickyTableHeader';
import { getInvoiceStatus, getInvoiceStatusColor } from 'containers/invoices/utils';
import { getEstimateStatusColor } from 'containers/estimates/utils';
import { EstimateStatus } from 'containers/estimates/constants';

const nestedColumnKeyId = [
  'customerName',
  'customerAddressStreet',
  'customerAddressCity',
  'customerAddressState',
  'customerAddressZipCode'
] as const;

type NestedColumnKeyId = typeof nestedColumnKeyId[number];

interface Column {
  id: keyof JobListItem | NestedColumnKeyId | 'actions' | 'checkbox';
  header:
    | string
    | React.ReactNode
    | ((props: { selectedJobs: JobListItem[]; jobs: JobListItem[]; onSelectAll: () => void }) => JSX.Element);
  accessor: (
    job: JobListItem & {
      selectedJobs?: JobListItem[];
      onJobCheckboxSelect?: (jobData: JobListItem) => void;
    }
  ) => React.ReactNode;
  width: string;
  fixed?: 'left' | 'right';
}

interface JobsTableProps {
  jobsList?: JobListItem[];
  groupedJobsByStatus?: Record<JobStatus, JobListItem[]>;
  isSelected: (id: string) => boolean;
  onJobClick: (jobId: string) => void;
  page: number;
  fetchData: (page: number) => void;
  jobsCount: number;
  onEditStatusClick: (jobId: string, status: JobStatus, jobNumber: string) => void;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSortBy?: string;
  currentSortOrder?: string;
  isGroupByStatus?: boolean;
  columnConfig?: ColumnConfig[];
  selectedJobs?: JobListItem[];
  onJobCheckboxSelect?: (job: JobListItem) => void;
  onSelectAll?: () => void;
  onSelectStatusGroup?: (status: JobStatus, jobs: JobListItem[]) => void;
  isSelectAllAcrossPagination?: boolean;
  deselectedJobIds?: string[];
  isJobEffectivelySelected?: (jobId: string) => boolean;
}

const fixedColumnSeperatorStyles = `first-letter:before:content-[""] before:absolute before:top-0 
 before:-left-[14px] before:bottom-0 before:w-[14px] before:bg-gradient-to-l from-[#162a400f] 
 to-transparent before:border-r before:border-borderGray`;

const JobsTable: FC<JobsTableProps> = ({
  isSelected,
  jobsList,
  groupedJobsByStatus,
  onJobClick,
  page,
  fetchData,
  onEditStatusClick,
  onSortChange,
  currentSortBy = 'created_at',
  currentSortOrder = 'DESC',
  isGroupByStatus = false,
  columnConfig = [],
  selectedJobs,
  onJobCheckboxSelect,
  onSelectAll,
  onSelectStatusGroup,
  isSelectAllAcrossPagination,
  deselectedJobIds,
  isJobEffectivelySelected
}) => {
  const [currentActionsPopoverId, setCurrentActionsPopoverId] = useState<string>(null);
  const [currentJob, setCurrentJob] = useState<JobListItem>(null);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [showDeleteJobConfirmation, setShowDeleteJobConfirmation] = useState(false);
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const { tableWrapperRef, tableHeaderRef } = useStickyTableHeader();
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement }>({});

  const currentTeam = useAppSelector(selectCurrentTeam);
  const jobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { modifyDoneJobs } = jobPermissions ?? {};
  const [deleteJobMutation, { isSuccess: isDeleteJobSuccess, isLoading: isDeleteJobLoading }] = useDeleteJobMutation();
  const [editJobScheduleMutation, { isLoading: isEditJobScheduleLoading }] = useEditJobScheduleMutation();
  const canMarkJobDone = jobPermissions?.closeOrMarkJobAsDone;

  useEffect(() => {
    if (!isDeleteJobLoading && isDeleteJobSuccess) {
      setCurrentJob(null);
      setShowDeleteJobConfirmation(false);
      fetchData(page);
    }
  }, [isDeleteJobLoading, isDeleteJobSuccess]);

  const getStatusChangeText = (status: JobStatus) => {
    switch (status) {
      case JobStatus.INBOX:
        return (
          <>
            <ScheduleJobIcon />
            <div>Submit job</div>
          </>
        );
      case JobStatus.SUBMITTED:
        return (
          <>
            <ScheduleJobIcon />
            <div>Confirm job</div>
          </>
        );
      case JobStatus.CONFIRMED:
      case JobStatus.PENDING:
        return (
          <>
            <SetInProgressIcon />
            <div>Set as in progress</div>
          </>
        );
      case JobStatus.IN_PROGRESS:
        if (canMarkJobDone) {
          return (
            <>
              <GreenTickIcon />
              <div>Mark job as done</div>
            </>
          );
        }

        return (
          <>
            <GreenTickIcon />
            <div>Mark job as done pending approval</div>
          </>
        );

      default:
        return null;
    }
  };

  const onStatusChange = (job: JobListItem) => {
    switch (job.status) {
      case JobStatus.INBOX:
        return onEditStatusClick(job.id, JobStatus.SUBMITTED, job.jobNumber);
      case JobStatus.SUBMITTED:
        return onEditStatusClick(job.id, JobStatus.CONFIRMED, job.jobNumber);

      case JobStatus.CONFIRMED:
      case JobStatus.PENDING:
        return onEditStatusClick(job.id, JobStatus.IN_PROGRESS, job.jobNumber);

      case JobStatus.IN_PROGRESS:
        if (canMarkJobDone) {
          return onEditStatusClick(job.id, JobStatus.DONE, job.jobNumber);
        }
        return onEditStatusClick(job.id, JobStatus.DONE_PENDING_APPROVAL, job.jobNumber);
      default:
        return null;
    }
  };

  const canShowDelete = (jobStatus: JobStatus, isEditable: boolean) => {
    if (!isEditable) {
      return false;
    }
    if (jobStatus === JobStatus.DONE) {
      return modifyDoneJobs;
    }
    return jobStatus !== JobStatus.REJECTED;
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListItemActionsMenu: FC<{ job: JobListItem }> = ({ job }) => {
    return (
      <div className="space-y-3 text-sbase font-semibold text-primaryText">
        {job.status !== JobStatus.DONE && job.status !== JobStatus.REJECTED && job?.isEditable && (
          <div
            id="jobs-table-3dot-share-job"
            className={'flex flex-row items-center gap-2'}
            onClick={e => {
              e.stopPropagation();
              onStatusChange(job);
              setCurrentActionsPopoverId(null);
            }}>
            {getStatusChangeText(job.status)}
          </div>
        )}
        <div
          id="jobs-table-3dot-show-directions"
          onClick={e => {
            e.stopPropagation();
            openInNewTab(`${GOOGLE_MAPS_BASE_URL}${getAddressAsString(job.customer.address)}`);
            setCurrentActionsPopoverId(null);
          }}
          className={'flex flex-row items-center gap-2'}>
          <DirectionsIcon />
          <div>Show directions</div>
        </div>
        {canShowDelete(job.status, job?.isEditable) && (
          <div
            id="jobs-table-3dot-delete-job"
            onClick={e => {
              e.stopPropagation();
              setCurrentJob(job);
              setShowDeleteJobConfirmation(true);
            }}
            className={'flex flex-row items-center gap-2 '}>
            <BinIcon />
            <div>Delete</div>
          </div>
        )}
      </div>
    );
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListActionsAnchor: React.FC<{ isPanelOpen: boolean }> = ({ isPanelOpen }) => {
    return (
      <div id="jobs-table-more-options" className="rounded-full p-2 hover:bg-gray-100">
        <ListActionsIcon
          className={twMerge(
            'hidden shrink-0 lg:flex',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
        <VerticalListActionsIcon
          className={twMerge(
            'mx-2 flex shrink-0 lg:hidden',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
      </div>
    );
  };

  const onToggleListItemActions = (jobId: string) => {
    if (jobId === currentActionsPopoverId) {
      setCurrentActionsPopoverId(null);
    } else {
      setCurrentActionsPopoverId(jobId);
      setTimeout(() => {
        const rowElement = rowRefs.current[jobId];
        if (rowElement) {
          const rowRect = rowElement.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const popoverHeight = 120;
          const margin = 20;

          if (rowRect.bottom > viewportHeight - popoverHeight - margin) {
            rowElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }
      }, 0);
    }
  };

  const isListItemActionsVisible = (jobId: string) => {
    return jobId === currentActionsPopoverId;
  };

  const onCloseListItemActionsPopover = (jobId: string) => {
    if (currentActionsPopoverId === jobId) {
      setCurrentActionsPopoverId(null);
    }
  };

  const handleSaveScheduleClick = (scheduledFrom: string, scheduledTo: string) => {
    editJobScheduleMutation({
      scheduledFrom,
      scheduledTo,
      jobId: currentJob.id,
      teamId: currentTeam.id
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        setOpenScheduleModal(false);
        fetchData(page);
      }
    });
  };

  const onDeleteJob = () => {
    deleteJobMutation({ jobId: currentJob.id, teamId: currentTeam.id });
    logAnalyticEvent(JobsCleverTapEvents.webDeleteJobConfirmation);
  };

  const handleJobClick = (jobId: string) => {
    setCurrentActionsPopoverId('');
    onJobClick(jobId);
  };

  const getStatusDate = (status: JobStatus, createdAt: string, updatedAt: string) => {
    if (status === JobStatus.SUBMITTED || status === JobStatus.CONFIRMED || status === JobStatus.INBOX) {
      return `Created on ${DateUtils.getDateInFormat({ date: new Date(createdAt), dateFormat: 'dd MMM yyyy' })}`;
    }

    return `Updated on  ${DateUtils.getDateInFormat({ date: new Date(updatedAt), dateFormat: 'dd MMM yyyy' })}`;
  };

  const handleSortClick = (field: string) => {
    if (!onSortChange) return;

    if (currentSortBy === field) {
      onSortChange(field, currentSortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      onSortChange(field, 'ASC');
    }
  };

  const getSortingProps = (columnId: string) => {
    let isTextColumn = [
      'customerName',
      'customerAddressStreet',
      'customerAddressCity',
      'customerAddressState',
      'createdBy'
    ].includes(columnId);

    // Automatically detect if it's a text column based on the type
    if (jobsList && jobsList.length > 0) {
      const sampleJob = jobsList[0];
      const value = sampleJob[columnId as keyof JobListItem];
      const avoidStringColumns = ['scheduledFrom', 'amount', 'jobNumber', 'createdAt'];
      if (typeof value === 'string' && !avoidStringColumns.includes(columnId)) {
        isTextColumn = true;
      }
    }

    const isActiveSort = currentSortBy === columnId;
    const shouldShowIcon = isActiveSort || hoveredColumn === columnId;
    const tooltipText = getSortTooltipText(columnId, currentSortBy, currentSortOrder, isTextColumn);
    const isColumnSortable = !['jobTags', 'assignees', 'checkbox', 'actions'].includes(columnId);
    return { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable };
  };

  const renderSortIcon = (isActiveSort: boolean, shouldShowIcon: boolean, isColumnSortable: boolean) => {
    if (!isColumnSortable) return null;
    const isDesc = currentSortOrder?.toUpperCase() === 'DESC';
    return (
      <div className="ml-1 cursor-pointer rounded-md py-1 px-1.5 hover:bg-gray-200">
        <SortByIcon
          className={`path path-fill-current rotate-180 transition-transform duration-200
              ${shouldShowIcon ? 'opacity-100' : 'opacity-0'} 
              ${isActiveSort ? 'text-primaryText' : 'text-accentText'} 
              ${isActiveSort && isDesc ? 'rotate-0' : ''}`}
        />
      </div>
    );
  };

  const getHeaderCheckBoxIcon = () => {
    const currentPageJobs = isGroupByStatus ? Object.values(groupedJobsByStatus || {}).flat() : jobsList || [];

    if (currentPageJobs.length === 0) return null;

    const currentPageJobIds = currentPageJobs.map(job => job.id);

    // If select all across pagination is active, check current page effective selection
    if (isSelectAllAcrossPagination) {
      const currentPageEffectivelySelected = currentPageJobIds.filter(jobId => !deselectedJobIds?.includes(jobId));
      const areAllCurrentPageJobsSelected = currentPageJobIds.length === currentPageEffectivelySelected.length;

      if (areAllCurrentPageJobsSelected) {
        return <CheckboxTickIcon className="h-3 w-3 text-white" />;
      }
      if (currentPageEffectivelySelected.length > 0) {
        return <HyphenIcon className="h-3 w-3 text-white" />;
      }
      return null;
    }

    // Regular selection logic
    const selectedCurrentPageJobs = selectedJobs?.filter(job => currentPageJobIds.includes(job.id)) || [];
    const areAllCurrentPageJobsSelected = currentPageJobIds.length === selectedCurrentPageJobs.length;

    if (areAllCurrentPageJobsSelected) {
      return <CheckboxTickIcon className="h-3 w-3 text-white" />;
    }
    if (selectedCurrentPageJobs.length > 0) {
      return <HyphenIcon className="h-3 w-3 text-white" />;
    }

    return null;
  };

  const getStatusGroupCheckboxIcon = (statusJobs: JobListItem[]) => {
    if (statusJobs.length === 0) return null;

    const statusJobIds = statusJobs.map(job => job.id);

    // If select all across pagination is active, check effective selection
    if (isSelectAllAcrossPagination) {
      const statusJobsEffectivelySelected = statusJobIds.filter(jobId => !deselectedJobIds?.includes(jobId));
      const areAllStatusJobsSelected = statusJobIds.length === statusJobsEffectivelySelected.length;

      if (areAllStatusJobsSelected) {
        return <CheckboxTickIcon className="h-3 w-3 text-white" />;
      }
      if (statusJobsEffectivelySelected.length > 0) {
        return <HyphenIcon className="h-3 w-3 text-white" />;
      }
      return null;
    }

    // Regular selection logic
    const selectedStatusJobs = selectedJobs?.filter(job => statusJobIds.includes(job.id)) || [];
    const areAllStatusJobsSelected = statusJobIds.length === selectedStatusJobs.length;

    if (areAllStatusJobsSelected) {
      return <CheckboxTickIcon className="h-3 w-3 text-white" />;
    }
    if (selectedStatusJobs.length > 0) {
      return <HyphenIcon className="h-3 w-3 text-white" />;
    }

    return null;
  };

  const handleSelectStatusGroup = (status: JobStatus, statusJobs: JobListItem[]) => {
    if (onSelectStatusGroup) {
      onSelectStatusGroup(status, statusJobs);
    }
  };

  // Define the new columns based on the image
  const columns: Column[] = [
    {
      id: 'checkbox',
      header: () => {
        const currentPageJobs = isGroupByStatus ? Object.values(groupedJobsByStatus || {}).flat() : jobsList || [];

        // If select all across pagination is active, check if current page jobs are effectively selected
        if (isSelectAllAcrossPagination) {
          const currentPageJobIds = currentPageJobs.map(job => job.id);
          const currentPageEffectivelySelected = currentPageJobIds.filter(jobId => !deselectedJobIds?.includes(jobId));
          const isCurrentPageSelected = currentPageEffectivelySelected.length > 0;

          return (
            <div
              onClick={e => {
                e.stopPropagation();
                onSelectAll?.();
              }}
              className="flex cursor-pointer items-center p-2">
              <CustomCheckbox
                id="select-all-jobs"
                className={isCurrentPageSelected ? '' : 'bg-white'}
                isChecked={isCurrentPageSelected}
                onClick={() => onSelectAll?.()}>
                {getHeaderCheckBoxIcon()}
              </CustomCheckbox>
            </div>
          );
        }

        // Check if all jobs on current page are selected
        const currentPageJobIds = currentPageJobs.map(job => job.id);
        const selectedCurrentPageJobs = selectedJobs?.filter(job => currentPageJobIds.includes(job.id)) || [];
        const hasAnyCurrentPageJobsSelected = selectedCurrentPageJobs.length > 0;

        return (
          <div
            onClick={e => {
              e.stopPropagation();
              onSelectAll?.();
            }}
            className="flex cursor-pointer items-center p-2">
            <CustomCheckbox
              id="select-all-jobs"
              className={hasAnyCurrentPageJobsSelected ? '' : 'bg-white'}
              isChecked={hasAnyCurrentPageJobsSelected}
              onClick={() => onSelectAll?.()}>
              {getHeaderCheckBoxIcon()}
            </CustomCheckbox>
          </div>
        );
      },
      accessor: job => {
        const isJobSelected = isJobEffectivelySelected
          ? isJobEffectivelySelected(job.id)
          : job.selectedJobs?.some(selectedJob => selectedJob.id === job.id) || false;

        return (
          <div
            className="flex cursor-pointer items-center p-2"
            onClick={e => {
              e.stopPropagation();
              job.onJobCheckboxSelect?.(job);
            }}>
            <CustomCheckbox
              className="shrink-0"
              id={`select-job-${job.id}`}
              isChecked={isJobSelected}
              onClick={() => job.onJobCheckboxSelect?.(job)}>
              {isJobSelected && <CheckboxTickIcon className="h-3 w-3 text-white" />}
            </CustomCheckbox>
          </div>
        );
      },
      width: 'w-[64px]'
    },
    {
      id: 'jobNumber',
      header: 'Job ID',
      accessor: (job: JobListItem) => (
        <span className="text-sbase font-semibold text-primaryText">{job.jobNumber.replaceAll('JOB-', '')}</span>
      ),
      width: 'w-[120px] min-w-[120px] max-w-[160px]'
    },
    {
      id: 'jobTags',
      header: 'Tags',
      accessor: (job: JobListItem) => <DynamicTagsDisplay job={job} />,
      width: 'w-[146px] min-w-[146px] max-w-[146px]'
    },
    {
      id: 'customerName',
      header: 'Customer',
      accessor: (job: JobListItem) => (
        <div className="flex flex-col gap-1 text-sbase text-primaryText">
          <span className="">{job.customer.name}</span>
          {job.customer.companyName && <span className="">{job.customer.companyName}</span>}
          {job.customer.address && <div className="text-accentText">{getAddressAsString(job.customer.address)}</div>}
        </div>
      ),
      width: 'w-[200px] min-w-[180px] max-w-[240px]'
    },
    {
      id: 'title',
      header: 'Job title',
      accessor: (job: JobListItem) => (
        <span className="break-words text-sbase font-semibold text-primaryText">{job.title}</span>
      ),
      width: 'w-[160px] min-w-[120px] max-w-[200px]'
    },
    {
      id: 'scheduledFrom',
      header: 'Schedule',
      accessor: (job: JobListItem) => (
        <div className="flex flex-col gap-1 text-sbase text-primaryText">
          {job.scheduledFrom ? (
            <>
              <div className="whitespace-nowrap">
                {`${DateUtils.getDayOfWeek(job.scheduledFrom)}, ${DateUtils.getDateInFormat({
                  date: new Date(job.scheduledFrom),
                  dateFormat: 'dd MMM yyyy'
                })}`}
              </div>
              <div>{`${DateUtils.formatTime(job.scheduledFrom)} - ${DateUtils.formatTime(job.scheduledTo)}`}</div>
              <div className="w-max rounded bg-secondaryBtn py-0.5 px-1.5 text-px13 text-accentText">
                {DateUtils.getDaysDifference(new Date(job.scheduledFrom))}
              </div>
            </>
          ) : (
            <div className="whitespace-nowrap text-accentText">Not yet confirmed</div>
          )}
        </div>
      ),
      width: 'w-[180px] min-w-[140px] max-w-[220px]'
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (job: JobListItem) => (
        <div className="min-w-[100px] max-w-[180px] text-sbase text-primaryText">
          <div className={'text-sbase font-bold'} style={{ color: getJobStatusColor(job.status) }}>
            {getJobStatusText(job.status)}
          </div>
          <div className="mt-1 text-[13px] text-accentText">
            {getStatusDate(job.status, job.createdAt, job.updatedAt)}
          </div>
        </div>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[160px]'
    },
    {
      id: 'invoiceStatus',
      header: 'Invoice status',
      accessor: (job: JobListItem) => (
        <div className="min-w-[100px] max-w-[180px] text-sbase text-primaryText">
          <div
            className={'text-sbase font-bold'}
            style={job.invoiceStatus ? { color: getInvoiceStatusColor(job.invoiceStatus) } : {}}>
            {job.invoiceStatus ? getInvoiceStatus(job.invoiceStatus) : '-'}
          </div>
        </div>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[160px]'
    },
    {
      id: 'estimateStatus',
      header: 'Estimate status',
      accessor: (job: JobListItem) => (
        <div className="min-w-[100px] max-w-[180px] text-sbase text-primaryText">
          <div
            className={'text-sbase font-bold capitalize'}
            style={job.estimateStatus ? { color: getEstimateStatusColor(job.estimateStatus as EstimateStatus) } : {}}>
            {job.estimateStatus ? job.estimateStatus?.toLowerCase() : '-'}
          </div>
        </div>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[160px]'
    },
    {
      id: 'externalCompanyName',
      header: 'External company',
      accessor: (job: JobListItem) => (
        <span className="text-sbase text-primaryText">{job.externalCompanyName || '-'}</span>
      ),
      width: 'w-[160px] min-w-[160px] max-w-[180px]'
    },
    {
      id: 'assignees',
      header: 'Team',
      accessor: (job: JobListItem) => (
        <div className="flex flex-col gap-1">
          {job.assignees?.length > 0 ? (
            job.assignees.map((member, index) => (
              <div key={member.id} className="text-sbase text-primaryText ">
                <span>{member.name}</span>
              </div>
            ))
          ) : (
            <div className="text-sbase text-primaryText">-</div>
          )}
        </div>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[180px]'
    },
    {
      id: 'serviceAreaName',
      header: 'Service area',
      accessor: (job: JobListItem) => <span className="text-sbase text-primaryText">{job.serviceAreaName || '-'}</span>,
      width: 'w-[140px] min-w-[120px] max-w-[180px]'
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: (job: JobListItem) => (
        <span className="text-sbase font-semibold text-primaryText">{getAmountWithCurrency(job.amount)}</span>
      ),
      width: 'w-[120px] min-w-[100px] max-w-[160px]'
    },
    {
      id: 'leadSourceType',
      header: 'Lead source',
      accessor: (job: JobListItem) => (
        <span className="text-sbase font-semibold text-primaryText">
          {JobLeadSourceTypes.find(type => type.value === job.leadSourceType)?.label || '-'}
          {job.leadSource && <span className="text-accentText"> - {job.leadSource}</span>}
        </span>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[180px]'
    },
    {
      id: 'createdBy',
      header: 'Created by',
      accessor: (job: JobListItem) => (
        <span className="text-sbase font-semibold text-primaryText">{job.createdBy?.name || '-'}</span>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[180px]'
    },
    {
      id: 'customerAddressStreet',
      header: 'Street address',
      accessor: (job: JobListItem) => (
        <span className="text-sbase text-primaryText">{job.customer?.address?.street || '-'}</span>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[180px]'
    },
    {
      id: 'customerAddressCity',
      header: 'City',
      accessor: (job: JobListItem) => (
        <span className="text-sbase text-primaryText">{job.customer?.address?.city || '-'}</span>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[180px]'
    },
    {
      id: 'customerAddressState',
      header: 'State',
      accessor: (job: JobListItem) => (
        <span className="text-sbase text-primaryText">{job.customer?.address?.state || '-'}</span>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[180px]'
    },
    {
      id: 'customerAddressZipCode',
      header: 'Zipcode',
      accessor: (job: JobListItem) => (
        <span className="text-sbase text-primaryText">{job.customer?.address?.zipCode || '-'}</span>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[180px]'
    },
    {
      id: 'createdAt',
      header: 'Created On',
      accessor: (job: JobListItem) => (
        <span className="text-sbase text-primaryText">
          {DateUtils.getDateInFormat({ date: new Date(job.createdAt), dateFormat: 'dd MMM yyyy' })}
        </span>
      ),
      width: 'w-[140px] min-w-[120px] max-w-[180px]'
    },
    {
      id: 'actions',
      header: '',
      accessor: (job: JobListItem) => (
        <CustomPopover
          showArrow={true}
          arrowType={PopoverArrowTypes.RIGHT}
          highlightAnchor={false}
          anchorComponent={ListActionsAnchor}
          zIndex="z-[101]"
          offset="right-12"
          margin="mt-1"
          onClosePopover={() => onCloseListItemActionsPopover(job.id)}
          show={isListItemActionsVisible(job.id)}
          onTogglePopover={() => onToggleListItemActions(job.id)}>
          {<ListItemActionsMenu job={job} />}
        </CustomPopover>
      ),
      width: 'w-[80px] min-w-[80px] max-w-[100px]',
      fixed: 'right'
    }
  ];

  // Get visible columns based on columnConfig configuration
  const getVisibleColumns = () => {
    let result = [...columns];

    if (columnConfig && columnConfig.length > 0) {
      const enabledColumnKeys = columnConfig.filter(col => col.isEnabled).map(col => col.columnKey);
      const essentialColumns = ['jobNumber', 'actions', 'checkbox'];

      result = result.filter(col => enabledColumnKeys.includes(col.id) || essentialColumns.includes(col.id));

      const checkboxCol = result.find(col => col.id === 'checkbox');
      const jobNumberCol = result.find(col => col.id === 'jobNumber');
      const actionsCol = result.find(col => col.id === 'actions');
      const otherCols = result.filter(col => !essentialColumns.includes(col.id));

      // Sort other columns by priority
      otherCols.sort((a, b) => {
        const aPriority = columnConfig.find(col => col.columnKey === a.id)?.priority ?? 999;
        const bPriority = columnConfig.find(col => col.columnKey === b.id)?.priority ?? 999;
        return aPriority - bPriority;
      });

      result = [
        ...(checkboxCol ? [checkboxCol] : []),
        ...(jobNumberCol ? [jobNumberCol] : []),
        ...otherCols,
        ...(actionsCol ? [actionsCol] : [])
      ];
    }

    return result;
  };

  const filteredColumns = getVisibleColumns();

  // Helper functions for column organization - only right fixed columns needed
  const rightFixedColumns = filteredColumns.filter(col => col.fixed === 'right');
  const scrollableColumns = filteredColumns.filter(col => !col.fixed);

  return (
    <>
      <div ref={tableWrapperRef} className={'h-full w-full grow overflow-x-auto'}>
        <table className="w-full table-fixed border-b">
          <thead ref={tableHeaderRef} className="sticky top-0 z-30 border-y border-secondaryBtn bg-[#F7F7F7]">
            <tr className="text-left [&>*]:py-1.5 [&>*]:pr-4 ">
              {scrollableColumns.map((col, idx) => {
                const { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable } = getSortingProps(col.id);
                return (
                  <th
                    key={col.id}
                    className={twMerge(
                      'font-semibold text-primaryText whitespace-nowrap',
                      col.width,
                      idx === 0 ? 'pl-[18px] !pr-2' : ''
                    )}
                    onMouseEnter={() => setHoveredColumn(col.id)}
                    onMouseLeave={() => setHoveredColumn(null)}>
                    <TooltipWrapper
                      tooltip={tooltipText}
                      position="bottom"
                      showTooltip={isColumnSortable}
                      className="w-fit">
                      <div
                        className={classnames('flex max-w-fit items-center rounded-md pl-1 text-sbase', {
                          'cursor-pointer hover:bg-gray-200': isColumnSortable
                        })}
                        onClick={() => isColumnSortable && handleSortClick(col.id)}>
                        {typeof col.header === 'function'
                          ? col.header({
                              selectedJobs: selectedJobs || [],
                              jobs: isGroupByStatus ? Object.values(groupedJobsByStatus || {}).flat() : jobsList || [],
                              onSelectAll: onSelectAll
                            })
                          : col.header}
                        {renderSortIcon(isActiveSort, shouldShowIcon, isColumnSortable)}
                      </div>
                    </TooltipWrapper>
                  </th>
                );
              })}

              {/* Spacer column to fill remaining space when there are fewer columns */}
              <th className="w-auto"></th>

              {/* Right Fixed Columns */}
              {rightFixedColumns.map(col => {
                const { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable } = getSortingProps(col.id);
                return (
                  <th
                    key={col.id}
                    className={twMerge(
                      'font-semibold text-primaryText whitespace-nowrap sticky right-0 z-20 bg-[#F7F7F7]',
                      'border-l border-secondaryBtn',
                      col.width,
                      fixedColumnSeperatorStyles
                    )}
                    onMouseEnter={() => setHoveredColumn(col.id)}
                    onMouseLeave={() => setHoveredColumn(null)}>
                    <TooltipWrapper
                      tooltip={tooltipText}
                      position="bottom"
                      showTooltip={isColumnSortable}
                      className="mb-1 w-fit">
                      <div
                        className={classnames('flex max-w-fit items-center rounded-md text-sbase', {
                          'cursor-pointer hover:bg-gray-200': isColumnSortable
                        })}
                        onClick={() => isColumnSortable && handleSortClick(col.id)}>
                        {typeof col.header === 'function'
                          ? col.header({
                              selectedJobs: selectedJobs || [],
                              jobs: isGroupByStatus ? Object.values(groupedJobsByStatus || {}).flat() : jobsList || [],
                              onSelectAll: onSelectAll
                            })
                          : col.header}
                        {renderSortIcon(isActiveSort, shouldShowIcon, isColumnSortable)}
                      </div>
                    </TooltipWrapper>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* Grouped by status rendering */}
            {isGroupByStatus &&
              groupedJobsByStatus &&
              !Array.isArray(groupedJobsByStatus) &&
              Object.entries(groupedJobsByStatus as Record<string, JobListItem[]>).map(([status, jobs]) => (
                <React.Fragment key={status}>
                  <tr style={{ backgroundColor: `${getJobStatusColor(status as JobStatus)}1A` }}>
                    <td
                      colSpan={scrollableColumns.length + 1}
                      className="py-1.5 pl-[30px] !pr-2 text-sbase font-semibold text-primaryText">
                      <div className="flex cursor-pointer items-center gap-2">
                        <CustomCheckbox
                          id={`select-status-${status}`}
                          width="min-w-[18px] h-[18px]"
                          className={
                            jobs.length > 0 &&
                            (isSelectAllAcrossPagination
                              ? jobs.some(job => !deselectedJobIds?.includes(job.id))
                              : selectedJobs?.filter(job => jobs.some(statusJob => statusJob.id === job.id)).length > 0)
                              ? ''
                              : 'bg-white'
                          }
                          isChecked={
                            jobs.length > 0 &&
                            (isSelectAllAcrossPagination
                              ? jobs.some(job => !deselectedJobIds?.includes(job.id))
                              : selectedJobs?.filter(job => jobs.some(statusJob => statusJob.id === job.id)).length > 0)
                          }
                          onClick={() => handleSelectStatusGroup(status as JobStatus, jobs)}>
                          {getStatusGroupCheckboxIcon(jobs)}
                        </CustomCheckbox>
                        {getJobStatusIconForGroupedJobs(status as JobStatus)}
                        <span className="whitespace-nowrap" style={{ color: getJobStatusColor(status as JobStatus) }}>
                          {getJobStatusText(status as JobStatus)} <span>({jobs.length})</span>
                        </span>
                      </div>
                    </td>
                    {/* Right Fixed Columns for group header */}
                    {rightFixedColumns.map(col => (
                      <td
                        key={col.id}
                        className={twMerge(
                          'sticky right-0 z-10 border-l bg-[#e9ecf2] text-sbase text-primaryText',
                          col.width,
                          fixedColumnSeperatorStyles
                        )}></td>
                    ))}
                  </tr>
                  {jobs.map((job, i) => (
                    <tr
                      key={job.id}
                      ref={el => (rowRefs.current[job.id] = el)}
                      className={twMerge(
                        'remove-highlight cursor-pointer border-secondaryBtn align-top [&>*]:py-3 [&>*]:pr-4',
                        isSelected(job.id) ? 'bg-secondaryBtn' : '',
                        i === jobs.length - 1 ? '' : 'border-b'
                      )}
                      onClick={() => handleJobClick(job.id)}>
                      {scrollableColumns.map((col, idx) => (
                        <td
                          key={col.id}
                          className={twMerge(
                            'text-sbase text-primaryText',
                            col.width,
                            idx === 0 ? 'pl-[22px] !pr-2 !py-1' : 'pl-1'
                          )}
                          style={{ height: col.id === 'jobTags' ? `${TAGS_CONTAINER_HEIGHT}px` : undefined }}
                          onClick={col.id === 'checkbox' ? e => e.stopPropagation() : undefined}>
                          {col.accessor({ ...job, selectedJobs, onJobCheckboxSelect })}
                        </td>
                      ))}

                      {/* Spacer cell */}
                      <td className="w-auto"></td>

                      {/* Right Fixed Columns */}
                      {rightFixedColumns.map(col => (
                        <td
                          key={col.id}
                          className={twMerge(
                            'text-sbase text-primaryText sticky right-0 bg-white z-10',
                            'border-l border-secondaryBtn py-3 pr-4',
                            col.width,
                            fixedColumnSeperatorStyles,
                            isSelected(job.id) ? 'bg-secondaryBtn' : ''
                          )}>
                          {col.accessor(job)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            {/* Flat list rendering (not grouped) */}
            {!isGroupByStatus &&
              jobsList &&
              Array.isArray(jobsList) &&
              jobsList.map((job, i) => (
                <tr
                  key={job.id}
                  ref={el => (rowRefs.current[job.id] = el)}
                  className={twMerge(
                    'remove-highlight cursor-pointer border-secondaryBtn align-top [&>*]:py-3 [&>*]:pr-4',
                    isSelected(job.id) ? 'bg-secondaryBtn' : '',
                    i === jobsList.length - 1 ? '' : 'border-b'
                  )}
                  onClick={() => handleJobClick(job.id)}>
                  {scrollableColumns.map((col, idx) => (
                    <td
                      key={col.id}
                      className={twMerge(
                        'text-sbase text-primaryText',
                        col.width,
                        idx === 0 ? 'pl-[22px] !py-1 !pr-2' : 'pl-1'
                      )}
                      style={{ height: col.id === 'jobTags' ? `${TAGS_CONTAINER_HEIGHT}px` : undefined }}
                      onClick={col.id === 'checkbox' ? e => e.stopPropagation() : undefined}>
                      {col.accessor({ ...job, selectedJobs, onJobCheckboxSelect })}
                    </td>
                  ))}

                  {/* Spacer cell */}
                  <td className="w-auto"></td>

                  {/* Right Fixed Columns */}
                  {rightFixedColumns.map(col => (
                    <td
                      key={col.id}
                      className={twMerge(
                        'text-sbase text-primaryText sticky right-0 bg-white z-10',
                        'border-l border-secondaryBtn py-3 pr-4',
                        col.width,
                        fixedColumnSeperatorStyles,
                        isSelected(job.id) ? 'bg-secondaryBtn' : ''
                      )}>
                      {col.accessor(job)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {openScheduleModal && (
        <EditScheduleModal
          onClose={() => setOpenScheduleModal(false)}
          onSave={handleSaveScheduleClick}
          scheduledFrom={currentJob.scheduledFrom}
          scheduledTo={currentJob.scheduledTo}
          isScheduled={!!currentJob.scheduledFrom}
          isLoading={isEditJobScheduleLoading}
        />
      )}
      {showDeleteJobConfirmation && (
        <DeleteJobConfirmationModal
          onCloseClick={() => setShowDeleteJobConfirmation(false)}
          handleDelete={onDeleteJob}
          processingDelete={isDeleteJobLoading}
        />
      )}
    </>
  );
};

export default JobsTable;
